import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminAnalytics } from '../../api/adminClient'
import {
  Building2,
  ClipboardCheck,
  CreditCard,
  TrendingUp,
  AlertCircle,
  Wallet,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Link } from 'react-router-dom'

export default function AdminDashboard() {
  const { t } = useTranslation('portal')
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminAnalytics.dashboard().then((r) => r.data),
  })
  const { data: recent } = useQuery({
    queryKey: ['admin', 'recent-activity'],
    queryFn: () => adminAnalytics.recentActivity().then((r) => r.data),
  })
  const { data: revenue } = useQuery({
    queryKey: ['admin', 'revenue', 14],
    queryFn: () => adminAnalytics.revenue(14).then((r) => r.data),
  })

  if (isLoading) return <div className="text-gray-500">{t('admin.common.loading')}</div>
  if (error) return <div className="text-red-600">{t('admin.dashboard.failedToLoad')}</div>

  const cards = [
    { label: t('admin.dashboard.totalCompanies'), value: data?.total_companies ?? 0, icon: Building2 },
    { label: t('admin.dashboard.pendingRegistrations'), value: data?.pending_registrations ?? 0, icon: ClipboardCheck },
    { label: t('admin.dashboard.activeCompanies'), value: data?.active_companies ?? 0, icon: Building2 },
    { label: t('admin.dashboard.suspended'), value: data?.suspended_companies ?? 0, icon: AlertCircle },
    { label: t('admin.dashboard.totalTransactions'), value: data?.total_transactions ?? 0, icon: CreditCard },
    { label: t('admin.dashboard.totalVolumeBam'), value: Number(data?.total_volume ?? 0).toFixed(2), icon: TrendingUp },
    { label: t('admin.dashboard.transactionsToday'), value: data?.transactions_today ?? 0, icon: CreditCard },
    { label: t('admin.dashboard.volumeTodayBam'), value: Number(data?.volume_today ?? 0).toFixed(2), icon: TrendingUp },
    { label: t('admin.dashboard.platformFeesBam'), value: Number(data?.platform_fees_collected ?? 0).toFixed(2), icon: Wallet },
    {
      label: t('admin.dashboard.successRateToday'),
      value: data?.payment_success_rate_today != null ? `${data.payment_success_rate_today.toFixed(1)}%` : '—',
      icon: TrendingUp,
    },
  ]

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.dashboard.overview')}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {cards.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="rounded-xl bg-white border border-gray-200 p-4 flex items-start gap-3 shadow-sm"
          >
            <div className="rounded-lg bg-emerald-100 p-2">
              <Icon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">{label}</p>
              <p className="text-xl font-semibold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {revenue && revenue.length > 0 && (
        <div className="rounded-xl bg-white border border-gray-200 p-4 mb-8 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('admin.dashboard.transactionVolumeLast14Days')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `${v}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                  labelStyle={{ color: '#374151' }}
                />
                <Line type="monotone" dataKey="transactions" stroke="#059669" name={t('admin.dashboard.chartTransactions')} />
                <Line type="monotone" dataKey="volume" stroke="#2563eb" name={t('admin.dashboard.chartVolumeBam')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('admin.dashboard.recentRegistrations')}</h2>
          <ul className="space-y-2">
            {(recent?.new_registrations ?? []).slice(0, 5).map((c) => (
              <li key={c.id} className="flex justify-between text-sm">
                <span className="text-gray-700">{c.name}</span>
                <Link to="/admin/registrations" className="text-emerald-600 hover:underline">
                  {t('admin.common.view')}
                </Link>
              </li>
            ))}
            {(!recent?.new_registrations?.length) && (
              <li className="text-gray-500 text-sm">{t('admin.dashboard.noRecentRegistrations')}</li>
            )}
          </ul>
        </div>
        <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('admin.dashboard.failedPaymentsLast7Days')}</h2>
          <ul className="space-y-2">
            {(recent?.failed_payments ?? []).slice(0, 5).map((p) => (
              <li key={p.id} className="flex justify-between text-sm">
                <span className="text-gray-700">{Number(p.amount).toFixed(2)} BAM</span>
                <span className="text-gray-500">{new Date(p.created_at).toLocaleDateString()}</span>
              </li>
            ))}
            {(!recent?.failed_payments?.length) && (
              <li className="text-gray-500 text-sm">{t('admin.dashboard.noFailedPayments')}</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}
